import { useState, forwardRef } from 'react';
import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  Card,
  Grid,
  Slide,
  Divider,
  Tooltip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableContainer,
  TableRow,
  TextField,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Zoom
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import ClearIcon from '@mui/icons-material/Clear';
import CheckIcon from '@mui/icons-material/Check';
import { useSnackbar } from 'notistack';

const DialogWrapper = styled(Dialog)(
  () => `
      .MuiDialog-paper {
        overflow: visible;
      }
`
);

const AvatarError = styled(Avatar)(
  ({ theme }) => `
      background-color: ${theme.colors.error.lighter};
      color: ${theme.colors.error.main};
      width: ${theme.spacing(12)};
      height: ${theme.spacing(12)};

      .MuiSvgIcon-root {
        font-size: ${theme.typography.pxToRem(45)};
      }
`
);


const ButtonError = styled(Button)(
  ({ theme }) => `
     background: ${theme.colors.error.main};
     color: ${theme.palette.error.contrastText};

     &:hover {
        background: ${theme.colors.error.dark};
     }
    `
);

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});


const applyFilters = (users, query, filters) => {
  return users.filter((user) => {
    let matches = true;

    if (query) {
      const properties = ['VAT', 'PIN', 'Company_name'];
      let containsQuery = false;

      properties.forEach((property) => {
        if (user[property] && user[property].toString().toLowerCase().includes(query.toLowerCase())) {
          containsQuery = true;
        }
      });

      if (filters.role && user.role !== filters.role) {
        matches = false;
      }

      if (!containsQuery) {
        matches = false;
      }
    }

    Object.keys(filters).forEach((key) => {
      const value = filters[key];

      if (value && user[key] !== value) {
        matches = false;
      }
    });

    return matches;
  });
};

const applyPagination = (users, page, limit) => {
  return users.slice(page * limit, page * limit + limit);
};

const Results = ({ users, ...rest }) => {
  const [selectedItems, setSelectedUsers] = useState([]);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();


  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [query, setQuery] = useState('');
  const [deleteID, setDeleteID] = useState(null);
  const [filters, setFilters] = useState({
    role: null
  });
  const [companyLimit, setCompanyLimit] = useState('');
  const [clientID, setClientID] = useState(null);
  const [openExtendLimit, setOpenExtendLimit] = useState(false);

  const handleConfirmExtendLimit = (id, limit) => {
    setCompanyLimit(limit);
    setClientID(id);
    setOpenExtendLimit(true);
  };

 const handleCompanyLimitChange = (e) => {
    setCompanyLimit(e.target.value);
  };

  const handleTabsChange = (event, tabsValue) => {
    let value = null;

    if (tabsValue !== 'all') {
      value = tabsValue;
    }

    setFilters((prevFilters) => ({
      ...prevFilters,
      role: value
    }));

    setSelectedUsers([]);
  };

  const handleQueryChange = (event) => {
    event.persist();
    setQuery(event.target.value);
  };

  const handleSelectAllUsers = (event) => {
    setSelectedUsers(event.target.checked ? users.map((user) => user.id) : []);
  };

  const handleSelectOneUser = (event, userId) => {
    if (!selectedItems.includes(userId)) {
      setSelectedUsers((prevSelected) => [...prevSelected, userId]);
    } else {
      setSelectedUsers((prevSelected) =>
        prevSelected.filter((id) => id !== userId)
      );
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleLimitChange = (event) => {
    setLimit(parseInt(event.target.value));
  };

  const filteredUsers = applyFilters(users, query, filters);
  const paginatedUsers = applyPagination(filteredUsers, page, limit);
  const selectedBulkActions = selectedItems.length > 0;
  const selectedSomeUsers =
    selectedItems.length > 0 && selectedItems.length < users.length;
  const selectedAllUsers = selectedItems.length === users.length;

  const [toggleView, setToggleView] = useState('table_view');

  const handleViewOrientation = (event, newValue) => {
    setToggleView(newValue);
  };

  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);

  const handleConfirmDelete = (id) => {
    setDeleteID(id);
    setOpenConfirmDelete(true);
  };

  const closeConfirmDelete = () => {
    setOpenConfirmDelete(false);
  };

  const handleExtendLimitClose = () => {
    setDeleteID(null);
    setOpenExtendLimit(false);
  };

  const handleExtendLimitSuccess = () => {
    window.makeRequest("PUT", `/api/Admin_CompaniesLimit?clientID=${clientID}`, {
      CompaniesLimit: companyLimit
    }, true, (callback) => {
      handleExtendLimitClose();
      rest.renderPortfolio();
    }, (err) => {
      if(err) {
        console.log('err', err);

      }
    });
  };

  const handleReject = (id, limit) =>{
    window.makeRequest("PUT", `/api/Admin_CompaniesLimit?clientID=${id}`, {
      CompaniesLimit: limit
    }, true, (callback) => {
      rest.renderPortfolio();
    }, (err) => {
      if(err) {
        console.log('err', err);

      }
    });
  };

  const handleDeleteCompleted = () => {
    window.makeRequest("DEL", `/api/Company?id=${deleteID}`, false, true, (callback) => {
      setOpenConfirmDelete(false);
      rest.delete(deleteID);
      setDeleteID(null);
    }, (err) => {
      if(err) {
        console.log('err', err);

      }
    });

    setOpenConfirmDelete(false);

    enqueueSnackbar(t('Компанијата е успешно избришана'), {
      variant: 'success',
      anchorOrigin: {
        vertical: 'top',
        horizontal: 'right'
      },
      TransitionComponent: Zoom
    });
  };

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent={{ xs: 'center', sm: 'space-between' }}
        pb={3}
      >

      </Box>

        <Card>

          <Divider />

          {paginatedUsers.length === 0 ? (
            <>
              <Typography
                sx={{ py: 10 }}
                variant="h3"
                fontWeight="normal"
                color="text.secondary"
                align="center"
              >
                {t("Не се пронајдени компании.")}
              </Typography>
            </>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('Име на компанија')}</TableCell>
                      <TableCell>{t('Даночен број')}</TableCell>
                      <TableCell>{t('Матичен број')}</TableCell>
                      <TableCell>{t('Лимит на компании')}</TableCell>
                      <TableCell>{t('Менаџирај')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedUsers.map((user) => {
                      const isUserSelected = selectedItems.includes(user.id);
                      return (
                        <TableRow hover key={user.id} selected={isUserSelected}>
                          <TableCell>
                            {user.Company_name}
                          </TableCell>
                          <TableCell>
                            {user.VAT}
                          </TableCell>
                          <TableCell>
                            {user.PIN}
                          </TableCell>
                          <TableCell>
                            {user.CompaniesLimit}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title={t('Прифати')} arrow>
                              <IconButton onClick={() => handleConfirmExtendLimit(user.ID, user.CompaniesLimit)}>
                                <CheckIcon fontSize="small"/>
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('Одбиј')} arrow>
                              <IconButton onClick={() => handleReject(user.ID, user.CompaniesLimit)}>
                                <ClearIcon fontSize="small"/>
                              </IconButton>
                            </Tooltip>

                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box p={2}>
                <TablePagination
                  component="div"
                  count={filteredUsers.length}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleLimitChange}
                  page={page}
                  rowsPerPage={limit}
                  rowsPerPageOptions={[5, 10, 15]}
                />
              </Box>
            </>
          )}
        </Card>


      <DialogWrapper
        open={openConfirmDelete}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Transition}
        keepMounted
        onClose={closeConfirmDelete}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          p={5}
        >
          <AvatarError>
            <CloseIcon />
          </AvatarError>

          <Typography align="center" sx={{ py: 4, px: 6 }} variant="h2">
            {t('Are you sure you want to permanently delete this user account')}
            ?
          </Typography>

          <Box>
            <Button
              variant="text"
              size="large"
              sx={{ mx: 1 }}
              onClick={closeConfirmDelete}
            >
              {t('Cancel')}
            </Button>
            <ButtonError
              onClick={handleDeleteCompleted}
              size="large"
              sx={{ mx: 1, px: 3 }}
              variant="contained"
            >
              {t('Delete')}
            </ButtonError>
          </Box>
        </Box>
      </DialogWrapper>

      <Dialog
        fullWidth
        maxWidth="md"
        open={openExtendLimit}
        onClose={handleExtendLimitClose}
      >
        <DialogTitle sx={{ p: 3 }}>
          <Typography variant="h4" gutterBottom>
            {('Зголемете го лимитот на компанијата')}
          </Typography>
          <Typography variant="subtitle2">
            {(
              'Пополнете го полето за да го зголемите лимитот на компанијата'
            )}
          </Typography>
        </DialogTitle>


        <DialogContent dividers sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={12}>
                  <TextField
                    fullWidth
                    label={('Лимит')}
                    name="Limit"
                    value={companyLimit}
                    onChange={handleCompanyLimitChange}
                    variant="outlined"
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button color="secondary" onClick={handleExtendLimitClose}>
            {('Откажи')}
          </Button>
          <Button
            type="submit"
            onClick={handleExtendLimitSuccess}
            variant="contained"
          >
            {('Зголеми лимит')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

Results.propTypes = {
  users: PropTypes.array.isRequired,
  delete: PropTypes.func.isRequired,
};

Results.defaultProps = {
  users: []
};

export default Results;
